import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import AnimatedValue from 'ember-animated/components/animated-value';
import fade from 'ember-animated/transitions/fade';

import Button from 'umg-ember-backend/components/ui/button';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

interface ModalSignature {
  Args: {
    dataDependencies?: unknown;
    isFullHeight?: boolean;
    onClose: () => void;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: [data: unknown, closeModal: () => void];
  };
}

export default class UiModalComponent extends Component<ModalSignature> {
  // Defaults
  fadeTransition = fade;
  @tracked modalsContainer: HTMLDivElement | null = null;
  @tracked isOpen = false;

  // Hooks
  constructor(owner: Owner, args: ModalSignature['Args']) {
    super(owner, args);

    this.isOpen = Boolean(this.args.dataDependencies);
  }

  // Getter and setter
  get isFullHeight() {
    return this.args.isFullHeight ?? true;
  }

  // Actions
  closeModal = () => {
    this.args.onClose();
  };

  updateIsOpen = () => {
    if (!this.args.dataDependencies) {
      this.isOpen = false;
    }
  };

  findModalsContainer = (element: HTMLDivElement) => {
    let modalsContainer;
    const uiWindow = element.closest('[data-selector="window"]');

    if (uiWindow) {
      modalsContainer = uiWindow.querySelector<HTMLDivElement>(
        ':scope > [data-selector="window-modals"]',
      );
    }

    this.modalsContainer = modalsContainer ?? element;

    next(this, function () {
      this.isOpen = true;
    });
  };

  // Template
  <template>
    {{#AnimatedValue
      @dataDependencies use=this.fadeTransition duration=400
      as |data|
    }}
      {{#if data}}
        <div
          {{didInsert this.findModalsContainer}}
          {{didUpdate this.updateIsOpen @dataDependencies}}
        >
          {{#if this.modalsContainer}}
            {{#in-element this.modalsContainer insertBefore=null}}
              <div
                class={{bem
                  (hash is-open=this.isOpen is-full-height=this.isFullHeight)
                }}
                aria-labelledby='ui-modal-label'
                aria-describedby='ui-modal-description'
                role='dialog'
                ...attributes
              >
                <div
                  class={{bem 'overlay'}}
                  role='button'
                  {{on 'click' this.closeModal}}
                ></div>
                <div class={{bem 'container'}}>
                  <div class={{bem 'content'}}>
                    <div class={{bem 'actions'}}>
                      <Button
                        @icon='close'
                        @style='outline'
                        @size='small'
                        {{on 'click' this.closeModal}}
                      />
                    </div>
                    {{yield data this.closeModal}}
                  </div>
                </div>
              </div>
            {{/in-element}}
          {{/if}}
        </div>
      {{else}}
        {{! https://github.com/ember-animation/ember-animated/issues/208 }}
        <div></div>
      {{/if}}
    {{/AnimatedValue}}
  </template>
}
